import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { EyeOff, Trash2, Pin, Eye } from 'lucide-react';
import { api, timeAgo, compact } from '../../lib/api';
import { SkeletonList } from '../../components/ui';

export default function AdminModeration() {
  const qc = useQueryClient();
  const [status, setStatus] = useState('active');
  const { data, isLoading } = useQuery({
    queryKey: ['adminPosts', status], queryFn: () => api.get('/admin/posts', { params: { status } }).then(r => r.data),
  });

  const statusM = useMutation({
    mutationFn: ({ id, st }) => api.post(`/admin/posts/${id}/status`, { status: st }),
    onSuccess: () => { toast.success('Updated'); qc.invalidateQueries(['adminPosts']); },
  });
  const pinM = useMutation({
    mutationFn: ({ id, pinned }) => api.post(`/admin/posts/${id}/pin`, { pinned }),
    onSuccess: () => { toast.success('Pin toggled'); qc.invalidateQueries(['adminPosts']); },
  });

  return (
    <div className="space-y-3">
      <div className="flex gap-1.5">
        {[['active', 'Active'], ['hidden', 'Hidden'], ['removed', 'Removed']].map(([k, l]) => (
          <button key={k} onClick={() => setStatus(k)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold ${status === k ? 'bg-ink-900 text-white' : 'bg-white border border-ink-200 text-ink-500'}`}>{l}</button>))}
      </div>

      {isLoading ? <SkeletonList n={6} h="h-24" /> : (
        <div className="space-y-2">
          {data.map(p => (
            <div key={p.id} className="card p-3">
              <div className="flex gap-3">
                {p.image_url && <img src={p.image_url} className="w-16 h-16 object-cover rounded-xl shrink-0" alt="" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="chip-ink">{p.type}</span>
                    {p.shop_name && <span className="chip-mint">{p.shop_name}</span>}
                    {p.group_handle && <span className="chip-brand">{p.group_handle}</span>}
                    {p.pinned ? <span className="chip bg-amber-100 text-amber-700">PINNED</span> : null}
                  </div>
                  {p.title && <p className="font-bold text-sm mt-1">{p.title}</p>}
                  <p className="text-xs text-ink-500 line-clamp-2 mt-0.5">{p.body}</p>
                  <p className="text-[11px] text-ink-400 font-semibold mt-1">
                    @{p.username} · {timeAgo(p.created_at)} · ❤️{p.likes_count} 💬{p.comments_count} 📢{p.shares_count} 👁{compact(p.views_count)}
                  </p>
                </div>
              </div>
              <div className="flex gap-1.5 mt-2">
                <Link to={`/post/${p.id}`} className="btn-ghost btn-sm flex-1"><Eye size={12} /> View</Link>
                <button onClick={() => pinM.mutate({ id: p.id, pinned: !p.pinned })} className="btn-ghost btn-sm flex-1">
                  <Pin size={12} /> {p.pinned ? 'Unpin' : 'Pin'}</button>
                {status !== 'hidden' && (
                  <button onClick={() => statusM.mutate({ id: p.id, st: 'hidden' })} className="btn-ghost btn-sm flex-1">
                    <EyeOff size={12} /> Hide</button>)}
                {status !== 'active' && (
                  <button onClick={() => statusM.mutate({ id: p.id, st: 'active' })} className="btn-mint btn-sm flex-1">Restore</button>)}
                <button onClick={() => statusM.mutate({ id: p.id, st: 'removed' })} className="btn btn-sm bg-red-50 text-red-600 flex-1">
                  <Trash2 size={12} /> Remove</button>
              </div>
            </div>))}
        </div>
      )}
    </div>
  );
}